"use client";

import { useCallback, useEffect, useId, useRef, useState } from "react";
import type { KeyboardEvent as ReactKeyboardEvent } from "react";
import { useSearchParams } from "react-router-dom";
import { BookOpen, Clock, ExternalLink, History, Send } from "lucide-react";
import { InlineNotice, PageHeader, focusRing } from "../ui";
import { WebhooksGate } from "./WebhooksGate";
import { WebhookAlertBanner } from "./WebhookAlertBanner";
import { useWebhookSummary } from "./useWebhookSummary";
import EndpointsTab, { useWebhookEndpoints } from "./EndpointsTab";
import type { SecretReveal } from "./SecretRevealCard";
import DeliveriesTab from "./DeliveriesTab";

// ────────────────────────────────────────────────────────────────────────────
// /developers/webhooks — endpoints management + delivery history.
//
//   ?tab=endpoints (default) | ?tab=deliveries   — synced with the tabs
//   ?new=1                                       — opens the create form
//   delivery filters (mode, endpoint, status, …) — owned by DeliveriesTab
//
// Access (business account → approved merchant) is WebhooksGate's job; this
// file only renders the approved state. The endpoint list lives here so the
// Deliveries tab can label its rows and a just-revealed signing secret
// survives switching tabs.
// ────────────────────────────────────────────────────────────────────────────

type TabId = "endpoints" | "deliveries";

const TABS: { id: TabId; label: string; icon: typeof Send }[] = [
  { id: "endpoints", label: "Endpoints", icon: Send },
  { id: "deliveries", label: "Deliveries", icon: History },
];

/** Route component for `/developers/webhooks`. Takes no props. */
export default function WebhooksPage() {
  return (
    <WebhooksGate>{({ wallet, email }) => <WebhooksPageBody wallet={wallet} email={email} />}</WebhooksGate>
  );
}

function WebhooksPageBody({ wallet, email }: { wallet: string; email: string | null }) {
  const [params, setParams] = useSearchParams();
  // `?new=1` always means "the create form", which lives on the Endpoints tab.
  const tab: TabId =
    params.get("new") === "1" ? "endpoints" : params.get("tab") === "deliveries" ? "deliveries" : "endpoints";

  const store = useWebhookEndpoints(wallet, email, { poll: tab === "endpoints" });
  const { summary } = useWebhookSummary(wallet, email);
  const [staleKey, setStaleKey] = useState(0);
  const [reveals, setReveals] = useState<SecretReveal[]>([]);
  const nonce = useRef(0);

  const platform = summary && summary.merchant ? summary.platform : null;
  const secretWritesBlocked = store.data.secretWritesBlocked || !!platform?.secretWritesBlocked;
  const workerDelayed = !!platform?.workerDelayed;

  // The shared summary changes when something happened elsewhere (the page
  // banner's "Mark as reviewed", another tab, a delivery failing): pull the
  // endpoint list right away instead of waiting for the 30 s poll.
  const summaryKey =
    summary && summary.merchant
      ? [summary.endpointCount, summary.enabledCount, summary.failingCount, summary.disabledCount, summary.failedDeliveriesUnacked].join("|")
      : "";
  const seenSummaryKey = useRef<string | null>(null);
  const { reload } = store;
  const listReady = store.data.status === "ready";
  useEffect(() => {
    if (!summaryKey) return;
    const first = seenSummaryKey.current === null;
    if (seenSummaryKey.current === summaryKey) return;
    seenSummaryKey.current = summaryKey;
    if (!first && listReady) void reload("silent");
  }, [summaryKey, listReady, reload]);

  const baseId = useId();
  const tabId = (t: TabId) => `${baseId}-tab-${t}`;
  const panelId = (t: TabId) => `${baseId}-panel-${t}`;
  const tabRefs = useRef<Record<TabId, HTMLButtonElement | null>>({ endpoints: null, deliveries: null });

  const selectTab = useCallback(
    (next: TabId) => {
      setParams((prev) => {
        const p = new URLSearchParams(prev);
        p.set("tab", next);
        p.delete("new");
        return p;
      });
    },
    [setParams],
  );

  const onTabKey = (e: ReactKeyboardEvent<HTMLButtonElement>) => {
    const i = TABS.findIndex((t) => t.id === tab);
    let j = -1;
    if (e.key === "ArrowRight") j = (i + 1) % TABS.length;
    else if (e.key === "ArrowLeft") j = (i - 1 + TABS.length) % TABS.length;
    else if (e.key === "Home") j = 0;
    else if (e.key === "End") j = TABS.length - 1;
    if (j === -1) return;
    e.preventDefault();
    const next = TABS[j].id;
    if (next !== tab) selectTab(next);
    tabRefs.current[next]?.focus();
  };

  const addReveal = useCallback((r: Omit<SecretReveal, "nonce">) => {
    nonce.current += 1;
    const entry: SecretReveal = { ...r, nonce: nonce.current };
    // Newest first; one card per endpoint (a second rotation replaces the first).
    setReveals((cur) => [entry, ...cur.filter((x) => x.endpointId !== r.endpointId)]);
  }, []);

  const dismissReveal = useCallback((endpointId: string) => {
    setReveals((cur) => cur.filter((x) => x.endpointId !== endpointId));
  }, []);

  const bumpDeliveries = useCallback(() => setStaleKey((k) => k + 1), []);

  const { data } = store;
  const counts = data.status === "ready" ? `${data.endpoints.length}/${data.limit || 5}` : null;

  return (
    <div className="max-w-5xl mx-auto py-8 sm:py-10 px-4">
      <PageHeader
        icon={<Send className="w-5 h-5 text-[#61CE70]" />}
        title="Webhooks"
        blurb="Get a signed POST the moment a checkout session completes, expires or fails."
        action={<DocsLink />}
      />

      <WebhookAlertBanner wallet={wallet} email={email} variant="page" />

      {secretWritesBlocked && (
        <InlineNotice tone="warn" role="status" className="mb-6" title="Webhooks are temporarily limited on our side.">
          Existing endpoints keep receiving events; creating endpoints and rotating secrets is paused. Nothing
          is lost.
        </InlineNotice>
      )}

      {workerDelayed && (
        <InlineNotice tone="info" icon={Clock} role="status" className="mb-6" title="Deliveries are running behind.">
          Our delivery queue is slower than usual right now. Every event is queued and will be delivered —
          nothing is lost.
        </InlineNotice>
      )}

      <div role="tablist" aria-label="Webhooks sections" className="flex border-b border-gray-700 mb-6">
        {TABS.map((t) => {
          const active = t.id === tab;
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              ref={(el) => {
                tabRefs.current[t.id] = el;
              }}
              type="button"
              role="tab"
              id={tabId(t.id)}
              aria-selected={active}
              aria-controls={panelId(t.id)}
              tabIndex={active ? 0 : -1}
              onClick={() => !active && selectTab(t.id)}
              onKeyDown={onTabKey}
              className={`flex items-center gap-2 px-3 sm:px-6 py-3 -mb-px text-sm font-medium whitespace-nowrap border-b-2 transition-colors rounded-t-md ${focusRing} ${
                active
                  ? "border-[#61CE70] text-[#61CE70]"
                  : "border-transparent text-gray-400 hover:text-gray-200"
              }`}
            >
              <Icon className="w-4 h-4 hidden sm:block" aria-hidden="true" />
              {t.label}
              {t.id === "endpoints" && counts && (
                <span
                  className={`text-[11px] font-semibold tabular-nums px-1.5 py-0.5 rounded-md ${
                    active ? "bg-[#61CE70]/10 text-[#61CE70]" : "bg-gray-800/80 text-gray-400"
                  }`}
                >
                  <span className="sr-only">(</span>
                  {counts}
                  <span className="sr-only"> endpoints used)</span>
                </span>
              )}
            </button>
          );
        })}
      </div>

      <div role="tabpanel" id={panelId(tab)} aria-labelledby={tabId(tab)}>
        {tab === "endpoints" ? (
          <EndpointsTab
            wallet={wallet}
            email={email}
            store={store}
            secretWritesBlocked={secretWritesBlocked}
            reveals={reveals}
            onReveal={addReveal}
            onDismissReveal={dismissReveal}
            onTestSent={bumpDeliveries}
          />
        ) : (
          <DeliveriesTab wallet={wallet} email={email} endpoints={data.endpoints} staleKey={staleKey} />
        )}
      </div>
    </div>
  );
}

function DocsLink() {
  return (
    <a
      href="/developers/docs#webhooks"
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Webhook documentation (opens in a new tab)"
      className={`inline-flex items-center gap-1.5 p-2.5 sm:px-3 sm:py-2 rounded-lg border border-gray-800 bg-gray-900/40 hover:border-gray-700 text-xs font-medium text-gray-300 hover:text-white transition-colors ${focusRing}`}
    >
      <BookOpen className="w-4 h-4 sm:w-3.5 sm:h-3.5" aria-hidden="true" />
      <span className="hidden sm:inline">Docs</span>
      <ExternalLink className="hidden sm:block w-3 h-3 text-gray-500" aria-hidden="true" />
    </a>
  );
}
